import { useEffect } from 'react'

import { SchedulePaymentMethodProps } from 'src/Components/SchedulePaymentMethod'
import { PaymentMethodEnum } from 'src/Constants/enums'
import { useAppDispatch, useAppSelector } from 'src/Hooks/redux'
import { useDefaultPaymentMethod } from 'src/Hooks/useDefaultPaymentMethod'
import { useGetBillingAgreementQuery } from 'src/Redux/billingAgreementApi'
import { setPaymentMethod } from 'src/Redux/payment-process'
import { getPaymentMethod } from 'src/Redux/payment-process/selectors'
import { changeRememberPayPalForFutureFlag } from 'src/Redux/paypalTransactions-process'
import { getRememberPayPalForFutureFlag } from 'src/Redux/paypalTransactions-process/selectors'
import {
  getIsEnoughBalanceForTiktok,
  getTiktokPrice,
} from 'src/Redux/schedule-process/audienceTiktokSlice/selectors'
import { CampaignPlatformTypeEnum, CampaignTypeEnum } from 'src/Types/campaigns'
import { paymentMethodChanged } from 'src/Helpers/TagManager'
import { getUserId } from 'src/Redux/auth-process/userSlice/selectors'
import { useGetSubscriptionQuery } from 'src/Redux/subscription-process/subscriptionApi'
import { getAmountOfCampaigns } from 'src/Redux/campaignList-process'
import { getScheduledTikTokCampaign } from 'src/Redux/schedule-process/tiktokCampaign/selectors'
import { HUNDRED } from 'src/Constants/numeric'
import {
  getCountry,
  isCountryLoaded,
} from 'src/Redux/auth-process/countrySlice/selectors'
import { getUserCountry } from 'src/Redux/auth-process/countrySlice/api-actions'

export const useTikTokSchedulePaymentMethod =
  (): SchedulePaymentMethodProps => {
    const dispatch = useAppDispatch()
    const { data: plan } = useGetSubscriptionQuery()
    const userId = useAppSelector(getUserId)
    const price = useAppSelector(getTiktokPrice)
    const scheduledTikTokCampaign = useAppSelector(getScheduledTikTokCampaign)
    const amountOfCampaigns = useAppSelector(getAmountOfCampaigns)
    const { data: billingData } = useGetBillingAgreementQuery()
    const paymentMethod = useAppSelector(getPaymentMethod)
    const isEnoughBalance = useAppSelector(getIsEnoughBalanceForTiktok)
    const isRememberPayPalForFuture = useAppSelector(
      getRememberPayPalForFutureFlag,
    )

    const countryCode = useAppSelector(getCountry)
    const countryLoaded = useAppSelector(isCountryLoaded)

    useEffect(() => {
      if (!countryLoaded) {
        dispatch(getUserCountry())
      }
    }, [dispatch, countryLoaded])

    useDefaultPaymentMethod({
      isEnoughBalance,
      isMetaUrl: false,
      hasBillingData: Boolean(billingData?.paypalEmail),
    })

    const handleChangePaymentMethod = (value: PaymentMethodEnum): void => {
      paymentMethodChanged({
        campaignId: Number(scheduledTikTokCampaign.id),
        campaignNumber: amountOfCampaigns,
        campaignPlatform: CampaignPlatformTypeEnum.TIKTOK,
        campaignType: scheduledTikTokCampaign.type || CampaignTypeEnum.BASIC,
        genreList: '',
        paymentMethod: value,
        prevPaymentMethod: paymentMethod,
        resultPrice: price || HUNDRED,
        userId: Number(userId),
        subscriptionPlan: plan?.package?.name || '',
      })
      dispatch(setPaymentMethod(value))

      localStorage.setItem('currentPaymentMethod', value)
    }

    const handleChangeHasSavePaypal = (value: boolean): void => {
      dispatch(changeRememberPayPalForFutureFlag(value))
    }
    return {
      paymentMethod,
      isEnoughBalance,
      countryCode,
      hasSavePaypal: isRememberPayPalForFuture,
      paypalEmail: billingData?.paypalEmail,
      onChangeHasSavePaypal: handleChangeHasSavePaypal,
      onChange: handleChangePaymentMethod,
    }
  }
